// Free-text search matching — THE single predicate for every search box.
//
// Every search in this app used to be a raw `field.toLowerCase().includes(query)`,
// copied into six files. That match is literal, so it breaks on the one thing the
// UI hides from you: whitespace. A client stored as "Megan  Cook" (two spaces —
// HTML collapses them, so the table looks perfectly normal) is found by "megan"
// and by "cook", but vanishes on "megan c", because what follows "megan" in the
// stored string is a second space, not a "c". That is not a hypothetical; it is
// how it was reported.
//
// Two rules fix the whole class:
//   1. NORMALISE both sides — lowercase, collapse every run of whitespace to one
//      space. JS `\s` covers tabs, newlines, non-breaking spaces ( ) and the
//      other Unicode spaces, so a name pasted out of a document normalises the
//      same as one that was typed.
//   2. MATCH EACH TERM INDEPENDENTLY against the joined fields. "megan c" is two
//      terms and both appear, so she matches. "cook megan" matches too — word
//      order stops mattering, which is what people expect from a search box.
//
// Terms never contain a space (they are produced by splitting on it) and the
// fields are joined WITH a space, so a term can never match across a field
// boundary. Multi-term queries deliberately can span fields — typing
// "megan elizabeth" finds the client whose name is Megan and whose company is
// Megan Elizabeth Films. That is the intended widening, not a bug.

/** Lowercase, collapse all whitespace runs to a single space, trim. */
export function normalizeForSearch(value) {
  if (value === null || value === undefined) return '';
  return String(value).toLowerCase().replace(/\s+/g, ' ').trim();
}

/**
 * True when EVERY whitespace-separated term in `query` appears somewhere in
 * `fields`. An empty/blank query matches everything (callers rely on this to
 * mean "no filter applied").
 *
 * @param {string} query   raw text from the search box
 * @param {Array}  fields  values to search across; nullish/empty entries ignored
 */
export function matchesSearch(query, fields) {
  const terms = normalizeForSearch(query).split(' ').filter(Boolean);
  if (terms.length === 0) return true;
  const haystack = (Array.isArray(fields) ? fields : [fields])
    .map(normalizeForSearch)
    .filter(Boolean)
    .join(' ');
  return terms.every((t) => haystack.includes(t));
}
